import React, { useState } from "react";
import styles from "../styles.module.scss";

interface MonthYearSelectProps {
  month: number | null | undefined;
  year: number | null | undefined;
  onSelect: (month: number | null | undefined, year: number) => void;
  className?: string;
  mode?: "yearly" | "monthly" | "all";
}

const MonthYearSelect: React.FC<MonthYearSelectProps> = ({
  onSelect,
  className,
  year,
  month,
  mode = "monthly",
}) => {
  const disabled = mode === "all";
  const currentDate = new Date();
  const currentYear = year || currentDate.getFullYear();
  const currentMonth = month || currentDate.getMonth() + 1;

  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const years = Array.from({ length: 6 }, (_, i) => currentDate.getFullYear() - i);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleSelect = () => {
    onSelect(mode === "yearly" ? null : selectedMonth, selectedYear);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    return mode === "all"
      ? "All time"
      : mode === "yearly"
      ? `${currentYear}`
      : `${months[(month || selectedMonth) - 1]} ${currentYear}`;
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      className={`${styles.selectContainer} ${className || ""} ${
        disabled ? styles.disabled : ""
      }`}
    >
      <div
        className={`${styles.selectButton} ${
          disabled ? styles.disabledButton : ""
        }`}
        onClick={handleToggleDropdown}
      >
        <span>{getDisplayText()}</span>
        {!disabled && (
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
          >
            <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" />
          </svg>
        )}
      </div>

      {isOpen && !disabled && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <div className={styles.yearSelect}>
              <label>Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {mode === "monthly" && (
              <div className={styles.monthSelect}>
                <label>Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {mode === "monthly" && (
            <div className={styles.monthGrid}>
              {months.map((month, index) => (
                <div
                  key={index + 1}
                  className={`${styles.monthItem} ${
                    selectedMonth === index + 1 ? styles.selected : ""
                  }`}
                  onClick={() => setSelectedMonth(index + 1)}
                >
                  {month.substring(0, 3)}
                </div>
              ))}
            </div>
          )}

          <div className={styles.dropdownFooter}>
            <button
              className={styles.cancelButton}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
            <button className={styles.applyButton} onClick={handleSelect}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearSelect;
